import { useState } from 'react';
import { ArrowLeft, ChevronRight, MapPin, Trash2 } from 'lucide-react';
import { orderedCategories, type Store } from '@milk/shared';
import { linkProps, navigate } from '../lib/router';
import { currentPosition } from '../lib/presence';
import { isNative } from '../lib/config';
import { addStore, deleteStore, setStoreView, updateStore, useStore } from '../store/store';
import { AisleOrder } from '../components/AisleOrder';
import { Empty, useToast } from '../components/ui';

export function StoresScreen() {
  const stores = useStore((s) => s.stores);
  const items = useStore((s) => s.items);
  const [name, setName] = useState('');
  const list = Object.values(stores).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  return (
    <>
      <div className="page-head">
        <a className="icon-btn" {...linkProps('/')} aria-label="Back to the list"><ArrowLeft size={22} aria-hidden /></a>
        <h1>Stores</h1>
      </div>
      <div className="card">
        <ul className="list-rows">
          {list.map((s) => {
            const only = Object.values(items).filter((i) => !i.checked && i.storeIds.includes(s.id)).length;
            return (
              <li key={s.id}>
                <a className="grow" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: 8, minHeight: 44 }} {...linkProps(`/stores/${s.id}`)}>
                  <span className="grow">
                    <strong>{s.name}</strong>
                    <span className="muted small" style={{ display: 'block' }}>
                      {[s.location ? 'Location set' : null, only ? `${only} item${only > 1 ? 's' : ''} only here` : null].filter(Boolean).join(' · ') || 'Tap to set its aisle order'}
                    </span>
                  </span>
                  <ChevronRight size={20} aria-hidden />
                </a>
              </li>
            );
          })}
        </ul>
        <form className="row" style={{ marginTop: 12 }} onSubmit={(e) => { e.preventDefault(); if (!name.trim()) return; const s = addStore(name); setName(''); navigate(`/stores/${s.id}`); }}>
          <label htmlFor="new-store" className="sr-only">New store name</label>
          <input id="new-store" className="grow" style={{ flex: 1, width: 'auto' }} value={name} onChange={(e) => setName(e.target.value)} placeholder="Add a store (Aldi, Costco…)" />
          <button className="btn primary" disabled={!name.trim()}>Add store</button>
        </form>
      </div>
    </>
  );
}

export function StoreEditor({ id }: { id: string }) {
  const store = useStore((s) => s.stores[id]);
  const categories = useStore((s) => s.categories);
  const count = useStore((s) => Object.keys(s.stores).length);
  if (!store) return <Empty title="That store is gone"><a className="btn" {...linkProps('/stores')}>All stores</a></Empty>;
  const ordered = orderedCategories(store, Object.values(categories));
  return (
    <>
      <div className="page-head">
        <a className="icon-btn" {...linkProps('/stores')} aria-label="Back to stores"><ArrowLeft size={22} aria-hidden /></a>
        <h1>{store.name}</h1>
      </div>
      <div className="card">
        <NameField store={store} />
      </div>
      <div className="card">
        <h2>Aisle order</h2>
        <p className="muted small" style={{ marginTop: 0 }}>The order you walk the store. The list follows it when you’re shopping here.</p>
        <AisleOrder categories={ordered} onChange={(ids) => updateStore(id, { aisleOrder: ids })} />
      </div>
      <PresenceCard store={store} />
      <div className="card">
        <div className="row">
          <button className="btn" onClick={() => { setStoreView(id); navigate('/'); }}>Shop here now</button>
          <button className="btn danger" disabled={count <= 1} onClick={() => {
            if (!confirmDelete(store.name)) return;
            deleteStore(id);
            navigate('/stores');
          }}><Trash2 size={18} aria-hidden /> Delete store</button>
        </div>
        {count <= 1 && <p className="muted small">You need at least one store.</p>}
      </div>
    </>
  );
}

const confirmDelete = (name: string) => window.confirm(`Delete ${name}? Items only for this store will be for any store.`);

function NameField({ store }: { store: Store }) {
  const [name, setName] = useState(store.name);
  return (
    <form className="row" onSubmit={(e) => { e.preventDefault(); if (name.trim() && name.trim() !== store.name) updateStore(store.id, { name: name.trim() }); }}>
      <label className="field grow">Name<input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => { if (name.trim() && name.trim() !== store.name) updateStore(store.id, { name: name.trim() }); }} /></label>
    </form>
  );
}

/** How the app knows you're here: GPS in the browser; Wi-Fi and Bluetooth for apps that can read them. */
function PresenceCard({ store }: { store: Store }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [wifi, setWifi] = useState(store.wifiNames.join(', '));
  const [beacons, setBeacons] = useState(store.beacons.join(', '));
  const split = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
  const here = async () => {
    setBusy(true);
    const pos = await currentPosition(true, true);
    setBusy(false);
    if (!pos) { toast('Couldn’t get your location. Check that location is allowed for this site.', 'error'); return; }
    updateStore(store.id, { location: { lat: pos.coords.latitude, lng: pos.coords.longitude, radiusM: store.location?.radiusM ?? 150 } });
    toast(`Saved ${store.name}’s location`);
  };
  return (
    <div className="card">
      <h2>Know when I’m here</h2>
      <p className="muted small" style={{ marginTop: 0 }}>When you open the list inside this store, it switches to this store’s order.</p>
      <div className="stack">
        <div className="row">
          <button className="btn" onClick={() => void here()} disabled={busy}><MapPin size={18} aria-hidden /> {store.location ? 'Update to where I am now' : 'I’m here now — save location'}</button>
          {store.location && <button className="btn ghost" onClick={() => updateStore(store.id, { location: null })}>Forget location</button>}
        </div>
        {store.location && (
          <label className="field">How far around it counts as “here”
            <select value={store.location.radiusM} onChange={(e) => updateStore(store.id, { location: { ...store.location!, radiusM: Number(e.target.value) } })}>
              {[75, 150, 300, 600].map((m) => <option key={m} value={m}>{m} m</option>)}
            </select>
          </label>
        )}
        <label className="field">Wi-Fi network names <span className="hint">Comma-separated, e.g. “Kroger Guest”.</span>
          <input value={wifi} onChange={(e) => setWifi(e.target.value)} onBlur={() => updateStore(store.id, { wifiNames: split(wifi) })} />
        </label>
        <label className="field">Bluetooth beacon IDs <span className="hint">Comma-separated.</span>
          <input value={beacons} onChange={(e) => setBeacons(e.target.value)} onBlur={() => updateStore(store.id, { beacons: split(beacons) })} />
        </label>
        {!isNative && <p className="muted small" style={{ margin: 0 }}>Web browsers can only use location. Wi-Fi and Bluetooth are saved for the iPhone and Mac apps.</p>}
      </div>
    </div>
  );
}
